import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import { LoadingSection } from "../../components/Spinner";

type AuditEntry = {
  id: string;
  actorEmail: string;
  action: string;
  resource: string;
  resourceId: string | null;
  createdAt: string;
};

const ACTION_LABELS: Record<string, { label: string; className: string }> = {
  create: { label: "Création", className: "bg-emerald-100 text-emerald-700" },
  update: { label: "Modification", className: "bg-amber-100 text-amber-700" },
  delete: { label: "Suppression", className: "bg-red-100 text-red-700" },
};

const RESOURCE_LABELS: Record<string, string> = {
  gyms: "Salle",
  coaches: "Coach",
  formats: "Format",
  schedule: "Planning",
  pricing: "Tarif",
  testimonials: "Témoignage",
  settings: "Apparence",
  pages: "Page",
  leads: "Message",
};

export function AdminAudit() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "audit"],
    queryFn: () => api<AuditEntry[]>("/admin/audit"),
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold uppercase">Journal</h1>
        <p className="mt-2 text-sm text-ink-500">
          Historique des actions réalisées dans l'espace d'administration (100 dernières).
        </p>
      </div>

      <div className="card overflow-x-auto">
        {isLoading && <LoadingSection />}

        {!isLoading && (
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wider text-ink-500">
                <th className="py-2 pr-4 font-semibold">Date</th>
                <th className="py-2 pr-4 font-semibold">Action</th>
                <th className="py-2 pr-4 font-semibold">Élément</th>
                <th className="py-2 font-semibold">Par</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {data?.map((e) => {
                const action = ACTION_LABELS[e.action] ?? {
                  label: e.action,
                  className: "bg-ink-100 text-ink-700",
                };
                const resource = RESOURCE_LABELS[e.resource] ?? e.resource;
                return (
                  <tr key={e.id}>
                    <td className="whitespace-nowrap py-3 pr-4 text-xs text-ink-500">
                      {new Date(e.createdAt).toLocaleString("fr-FR")}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${action.className}`}
                      >
                        {action.label}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="font-semibold">{resource}</span>
                      {e.resourceId && (
                        <span className="ml-1 text-xs text-ink-500">#{e.resourceId.slice(0, 8)}</span>
                      )}
                    </td>
                    <td className="py-3 text-xs text-ink-700">{e.actorEmail}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!isLoading && data?.length === 0 && (
          <p className="py-6 text-center text-sm text-ink-500">Aucune action enregistrée.</p>
        )}
      </div>
    </div>
  );
}
